// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Divider, Input, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'

import Button from '../../../core/components/Button.jsx'
import { saveReportCustomization } from '../services/documentReportsService.ts'
import type {
  CustomizationData,
  PerformanceCustomizationContext
} from '../../../state_manager/data_models/document_report_customization_model.ts'

const { TextArea } = Input

/** One key-driver card's draft: empty accountId = no card in that slot;
 *  empty chip/blurb = the generated text for the selected company. */
interface DriverDraft {
  accountId: string | undefined
  chip: string
  blurb: string
}

interface PerformanceCustomizeBodyProps {
  context: PerformanceCustomizationContext
  reportCode: string
  accessToken: string | null
  onClose: () => void
  /** Called after a successful save so the parent can refresh the preview. */
  onSaved: () => void
}

const SECTION_TITLE_CLASS = 'shrink-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500'

/**
 * The Investor Performance Report's Customize body (owner-approved design
 * 2026-08-16): edit the deck's Period Highlights, pick which companies appear
 * as Key Drivers, and edit each card's chip/blurb. Metrics rows stay computed
 * and are not editable here.
 */
export function PerformanceCustomizeBody({
  context,
  reportCode,
  accessToken,
  onClose,
  onSaved
}: PerformanceCustomizeBodyProps) {
  const [saving, setSaving] = useState(false)

  // The highlight baseline (what renders with no saved edit) is the approved
  // LP period highlight set when one exists for this period (LP agent
  // programme Phase 2), else the generated sentences. Labels follow it.
  const baselineLabel = context.approved_set
    ? `approved highlights v${context.approved_set.version}`
    : 'generated'

  // Highlight drafts hold the EFFECTIVE text (saved override or generated) so
  // editing starts from the sentence on the slide; a slot equal to its
  // generated sentence saves as null (= stays live). Driver chip/blurb drafts
  // hold the OVERRIDE only ('' = generated), because the generated text
  // follows whichever company the card's picker selects.
  const [highlights, setHighlights] = useState<string[]>(() => {
    const savedData = context.saved?.data
    return Array.from({ length: context.limits.highlight_slots }, (_, i) => {
      const savedSlot = savedData?.highlights?.[i]
      if (typeof savedSlot === 'string' && savedSlot.trim()) return savedSlot
      return context.generated.highlights[i] ?? ''
    })
  })
  const [drivers, setDrivers] = useState<DriverDraft[]>(() => {
    const savedData = context.saved?.data
    const basePicks = savedData?.key_drivers?.length
      ? savedData.key_drivers.map((d) => ({
          accountId: d.account_id,
          chip: d.chip ?? '',
          blurb: d.blurb ?? ''
        }))
      : context.generated.key_drivers.map((d) => ({
          accountId: d.account_id ?? undefined,
          chip: '',
          blurb: ''
        }))
    return Array.from({ length: context.limits.cards }, (_, i) =>
      basePicks[i] ?? { accountId: undefined, chip: '', blurb: '' }
    )
  })

  const poolByAccountId = useMemo(
    () => new Map(context.pool.map((entry) => [entry.account_id, entry])),
    [context]
  )

  const poolOptions = useMemo(
    () =>
      context.pool.map((entry) => ({
        value: entry.account_id,
        label: `${entry.name} (${entry.delta_label})`
      })),
    [context]
  )

  const generatedPickIds = useMemo(
    () => context.generated.key_drivers.map((d) => d.account_id ?? undefined),
    [context]
  )

  /** The blob the current drafts amount to — all-generated collapses to the
   *  empty payload, which the backend stores as "clear the row". */
  const draftData: CustomizationData = useMemo(() => {
    const generated = context.generated.highlights
    const highlightSlots = highlights.map((text, i) => {
      const trimmed = text.trim()
      if (!trimmed || trimmed === (generated[i] ?? '').trim()) return null
      return trimmed
    })
    const cards = drivers
      .filter((d) => d.accountId)
      .map((d) => ({
        account_id: d.accountId as string,
        chip: d.chip.trim() || null,
        blurb: d.blurb.trim() || null
      }))
    const cardsAreGenerated =
      cards.every((c) => !c.chip && !c.blurb) &&
      cards.length === generatedPickIds.filter(Boolean).length &&
      cards.every((c, i) => c.account_id === generatedPickIds[i])
    return {
      highlights: highlightSlots,
      key_drivers: cardsAreGenerated ? null : cards
    }
  }, [context, highlights, drivers, generatedPickIds])

  const handleSave = useCallback(async () => {
    setSaving(true)
    const result = await saveReportCustomization(
      {
        reportCode,
        investorAccountId: context.investor_account_id,
        periodStart: context.period.start,
        periodEnd: context.period.end,
        data: draftData
      },
      accessToken
    )
    setSaving(false)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to save customization')
      return
    }
    toast.success(
      result.saved ? 'Customization saved' : 'Customization cleared — report is fully generated'
    )
    onSaved()
    onClose()
  }, [context, reportCode, draftData, accessToken, onSaved, onClose])

  const handleResetAll = useCallback(() => {
    setHighlights(
      Array.from(
        { length: context.limits.highlight_slots },
        (_, i) => context.generated.highlights[i] ?? ''
      )
    )
    setDrivers(
      Array.from({ length: context.limits.cards }, (_, i) => ({
        accountId: context.generated.key_drivers[i]?.account_id ?? undefined,
        chip: '',
        blurb: ''
      }))
    )
  }, [context])

  const setHighlight = useCallback((index: number, value: string) => {
    setHighlights((prev) => prev.map((v, i) => (i === index ? value : v)))
  }, [])

  const setDriver = useCallback((index: number, patch: Partial<DriverDraft>) => {
    setDrivers((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }, [])

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
        <Button
          type='text'
          variant='default'
          size='small'
          icon={<FontAwesomeIcon icon={faArrowRotateLeft} className='text-xs' />}
          onClick={handleResetAll}
        >
          Reset all to {baselineLabel}
        </Button>
        <div className='flex items-center gap-1.5'>
          <Button variant='cancel' size='small' onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant='save'
            size='small'
            loading={saving}
            onClick={() => {
              void handleSave()
            }}
          >
            Save
          </Button>
        </div>
      </div>
      <Divider className='!mt-1 !mb-2 shrink-0' />

      <div className='min-h-0 flex-1 overflow-y-auto pr-1'>
        <Alert
          type='warning'
          showIcon
          className='mb-2'
          message='Edited text is frozen'
          description='Generated sentences embed live figures and keep up with the data. Text you edit stays exactly as written — if the underlying data changes later, an edited bullet can contradict the computed tiles.'
        />
        {context.saved && (
          <div className='mb-2 text-xs text-gray-500'>
            Last edited
            {context.saved.modified_by_name ? ` by ${context.saved.modified_by_name}` : ''}
            {' on '}
            {new Date(context.saved.date_modified).toLocaleDateString('en-US')}
            . Shared — everyone running this report for this investor and period sees these
            edits.
          </div>
        )}

        <div className={SECTION_TITLE_CLASS}>Period Highlights — {context.period.label}</div>
        <div className='mb-1 text-xs text-gray-500'>
          Four bullets, about two lines each. Use **double asterisks** to bold a name or figure.
          {' '}Baseline:{' '}
          {context.approved_set ? (
            <span data-testid='highlights-baseline'>
              Approved highlights v{context.approved_set.version}
              {context.approved_set.approved_at
                ? ` (approved ${new Date(context.approved_set.approved_at).toLocaleDateString('en-US')})`
                : ''}
              . Edit the set on Reporting → Period Highlights to change the baseline for every run.
            </span>
          ) : (
            <span data-testid='highlights-baseline'>
              Generated — no approved LP highlight set for this period yet.
            </span>
          )}
        </div>
        {highlights.map((text, i) => {
          const generated = context.generated.highlights[i] ?? ''
          const isCustom = text.trim() !== generated.trim()
          return (
            <div key={i} className='mb-2'>
              <div className='mb-0.5 flex items-center justify-between'>
                <span className='text-xs font-medium text-gray-600'>Bullet {i + 1}</span>
                {isCustom && (
                  <Button
                    type='text'
                    variant='default'
                    size='small'
                    className='!h-5 !px-1 !text-[11px]'
                    onClick={() => setHighlight(i, generated)}
                  >
                    Reset to {baselineLabel}
                  </Button>
                )}
              </div>
              <TextArea
                value={text}
                onChange={(e) => setHighlight(i, e.target.value)}
                maxLength={context.limits.highlight_max}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder={generated || `No ${baselineLabel} bullet for this slot`}
              />
            </div>
          )
        })}

        <Divider className='!my-2' />
        <div className={SECTION_TITLE_CLASS}>Key Drivers</div>
        <div className='mb-1 text-xs text-gray-500'>
          Three spotlight cards. Pick any company in the run (period move shown); leave the
          chip and blurb empty to keep the generated text. Card metrics stay computed.
        </div>
        {drivers.map((driver, i) => {
          const poolEntry = driver.accountId ? poolByAccountId.get(driver.accountId) : undefined
          const isCustom =
            driver.accountId !== generatedPickIds[i] || Boolean(driver.chip) || Boolean(driver.blurb)
          return (
            <div key={i} className='mb-2 rounded border border-gray-200 p-2'>
              <div className='mb-1 flex items-center justify-between'>
                <span className='text-xs font-medium text-gray-600'>Card {i + 1}</span>
                {isCustom && (
                  <Button
                    type='text'
                    variant='default'
                    size='small'
                    className='!h-5 !px-1 !text-[11px]'
                    onClick={() =>
                      setDriver(i, {
                        accountId: generatedPickIds[i],
                        chip: '',
                        blurb: ''
                      })
                    }
                  >
                    Reset to generated
                  </Button>
                )}
              </div>
              <Select
                className='mb-1.5 w-full'
                size='small'
                showSearch
                allowClear
                optionFilterProp='label'
                placeholder='Select company'
                value={driver.accountId}
                options={poolOptions}
                onChange={(value) =>
                  setDriver(i, { accountId: value ?? undefined, chip: '', blurb: '' })
                }
              />
              <Input
                className='mb-1.5'
                size='small'
                value={driver.chip}
                onChange={(e) => setDriver(i, { chip: e.target.value })}
                maxLength={context.limits.chip_max}
                showCount
                disabled={!driver.accountId}
                placeholder={poolEntry ? `Chip — generated: ${poolEntry.chip}` : 'Chip'}
              />
              <TextArea
                value={driver.blurb}
                onChange={(e) => setDriver(i, { blurb: e.target.value })}
                maxLength={context.limits.blurb_max}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
                disabled={!driver.accountId}
                placeholder={
                  poolEntry
                    ? poolEntry.blurb
                      ? `Blurb — generated: ${poolEntry.blurb}`
                      : 'Blurb — no generated description for this company'
                    : 'Blurb'
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
